/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'
import { withRouter } from 'react-router-dom'
import { flatten } from 'lodash'

import {
  TabbedView,
  ScopedTabbedView,
  EmployerPayrollCycleTabs
} from '../'

import {
  getNodeTypeFromBase64Id,
  getUniqueValuesInArrayOfObjectsByProperty
} from '../../utils'

const identityTypeToPrimaryTabsMapping = {
  'client': [
    { name: 'summary', label: 'Summary' },
    { name: 'earnbacks', label: 'EarnBacks' },
    { name: 'employment', label: 'Employment' },
    { name: 'contact', label: 'Contact' },
    { name: 'banking', label: 'Banking' },
    { name: 'products', label: 'Products' },
    { name: 'requests', label: 'Requests' },
    { name: 'cases', label: 'Cases' },
    { name: 'payroll', label: 'Payroll' },
    { name: 'documents', label: 'Documents' },
    { name: 'communications', label: 'Communications' },
    { name: 'accounts', label: 'Accounts' },
    { name: 'insights', label: 'Insights' }
  ],
  'employer': [
    { name: 'summary', label: 'Summary' },
    { name: 'solution', label: 'Solution & Products' },
    { name: 'contact', label: 'Contact' },
    { name: 'banking', label: 'Banking' },
    { name: 'cases', label: 'Cases' },
    { name: 'documents', label: 'Documents' },
    { name: 'accounts', label: 'Accounts' },
    { name: 'contacts', label: 'Contacts' },
    { name: 'divisions', label: 'Divisions' },
    { name: 'employees', label: 'Employees' },
    { name: 'payrollcycles', label: 'Payroll cycles'},
    { name: 'insights', label: 'Insights' }
  ],
  'contact': [
    { name: 'summary', label: 'Summary' },
    { name: 'contact', label: 'Contact' },
    { name: 'responsibilities', label: 'Roles & responsibilities' },
    { name: 'communications', label: 'Communications' }
  ]
}

const getPrimaryTab = (tab) => (
  {
    'credit': 'products',
    'investments': 'products',
    'withdrawals': 'products',
    'insurance': 'products',
    'messages': 'communications',
    'emails': 'communications',
    'whatsapp': 'communications',
    'latest': 'insights',
    'comparison': 'insights',
    'payrollruns': 'payroll',
    'payslips': 'payroll'
  }[tab] ||
  (
    getNodeTypeFromBase64Id(tab) === 'PayrollCycleNode'
      ? 'payrollcycles'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'LoanNode' || getNodeTypeFromBase64Id(tab) === 'CreditNode'
      ? 'credit'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'ClientInvestmentNode'
      ? 'investments'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'ClientFuneralPolicyNode'
      ? 'insurance'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'CaseNode'
      ? 'cases'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'RequestRunNode'
      ? 'requests'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'AccountNode'
      ? 'accounts'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'WhatsappMessageNode'
      ? 'communications'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'WithdrawalNode'
      ? 'withdrawals'
      : null
  )
)

const hasSecondaryTabs = (tabName) => (
  ['products', 'communications', 'payrollcycles', 'insights', 'payroll'].indexOf(tabName) > -1
)

const getActivePrimaryTab = (initialTab) => {
  if (initialTab) {
    return getUniqueValuesInArrayOfObjectsByProperty(
      flatten(Object.values(identityTypeToPrimaryTabsMapping)),
      'name'
    ).indexOf(initialTab) > -1
      ? initialTab
      : getPrimaryTab(initialTab) || 'summary'
  } else {
    return null
  }
}

class IdentityPageNavigation extends React.PureComponent {
  static propTypes = {
    identityId: PropTypes.string.isRequired,
    identityTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
    onTabClick: PropTypes.func.isRequired,
    initialTab: PropTypes.string
  }

  state = {
    // assign activePrimaryTab props.initialTab's value if that value is an available primary listed in identityTypeToPrimaryTabsMapping
    activePrimaryTab: getActivePrimaryTab(this.props.initialTab)
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.initialTab !== this.props.initialTab) {
      this.setState({ activePrimaryTab: getActivePrimaryTab(this.props.initialTab) })
    }
  }

  getPrimaryTabs = (identityTypes) => {
    let allTabs = []

    Object.keys(identityTypeToPrimaryTabsMapping).forEach(identityType => {
      if (identityTypes.includes(identityType)) {
        allTabs = [...allTabs, ...identityTypeToPrimaryTabsMapping[identityType]]
      }
    })

    if (identityTypes.length > 1) {
      // if identity has multiple identity types, get unique tabs across identity types to display
      const uniqueTabs = allTabs.filter((tab, index) => (
        index === allTabs.findIndex(obj => (
          JSON.stringify(obj) === JSON.stringify(tab)
        ))
      ))
      return uniqueTabs
    } else {
      return allTabs
    }
  }

  getSecondaryTabs = (primaryTab) => {
    if (primaryTab === 'products') {
      let subTabs = ['Credit', 'Investments', 'Withdrawals', 'Insurance']
      let currentTab = this.props.location.pathname.split('/').pop().toLowerCase()
      if (currentTab === 'products') {
        this.props.history.push(`/os/identity/${this.props.identityId}/products/credit`)
        this.props.onTabClick('credit')  // initialise default view
      } else {
        this.props.onTabClick(currentTab)
      }
      let initialActiveTabIndex = ['credit', 'investments', 'withdrawals', 'insurance'].indexOf(currentTab)
      return (
        <ScopedTabbedView
          tabs={subTabs}
          contents={[]}
          onClick={tabIndex => {
            let productTab = subTabs[tabIndex].toLowerCase()
            this.props.history.push(
              `/os/identity/${this.props.identityId}/products/${productTab}`
            )
            this.props.onTabClick(productTab)
          }}
          initialActiveTabIndex={initialActiveTabIndex > -1
            ? initialActiveTabIndex
            : null
          }
        />
      )
    } else if (primaryTab === 'payrollcycles') {
      return (
        <Query
          query={
            gql`
              query($id: ID!) {
                identity(id: $id) {
                  id
                  employer {
                    id
                  }
                }
              }
            `
          }
          variables={{ id: this.props.identityId }}>
          {({ loading, error, data}) => {
            if (loading || error) {
              return null
            }

            return (
              <EmployerPayrollCycleTabs
                employerId={data.identity.employer.id}
                onClick={payrollCycleId => {
                  this.props.history.push(`/os/identity/${this.props.identityId}/payrollcycles/${payrollCycleId}`)
                  this.props.onTabClick('payrollcycles', payrollCycleId)
                }}
                initialActiveTab={this.props.initialTab}
              />
            )
          }}
        </Query>
      )
    } else if (primaryTab === 'communications'){
      let subTabs = ['Messages', 'Emails', 'WhatsApp']
      let currentTab = this.props.location.pathname.split('/').pop().toLowerCase()
      if (currentTab === 'communications') {
        this.props.history.push(
          `/os/identity/${this.props.identityId}/communications/messages`
        )
        this.props.onTabClick('messages')  // initialise default view
      }
      let initialActiveTabIndex = ['messages', 'emails', 'whatsapp'].indexOf(currentTab)

      return (
        <ScopedTabbedView
          tabs={subTabs}
          contents={[]}
          onClick={tabIndex => {
            let productTab = subTabs[tabIndex].toLowerCase()
            this.props.history.push(
              `/os/identity/${this.props.identityId}/communications/${productTab}`
            )
            this.props.onTabClick(productTab)
          }}
          initialActiveTabIndex={initialActiveTabIndex > -1
            ? initialActiveTabIndex
            : null
          }
        />
      )
    } else if (primaryTab === 'insights') {
      let subTabs = ['Latest', 'Comparison']
      let currentTab = this.props.location.pathname.split('/').pop().toLowerCase()
      if (currentTab === 'insights') {
        this.props.history.push(
          `/os/identity/${this.props.identityId}/insights/latest`
        )
        this.props.onTabClick('latest')  // initialise default view
      }
      let initialActiveTabIndex = ['latest', 'comparison'].indexOf(currentTab)

      return (
        <ScopedTabbedView
          tabs={subTabs}
          contents={[]}
          onClick={tabIndex => {
            let productTab = subTabs[tabIndex].toLowerCase()
            this.props.history.push(
              `/os/identity/${this.props.identityId}/insights/${productTab}`
            )
            this.props.onTabClick(productTab)
          }}
          initialActiveTabIndex={initialActiveTabIndex > -1
            ? initialActiveTabIndex
            : null
          }
        />
      )
    } else if (primaryTab === 'payroll'){
      let subTabs = ['Payroll runs', 'Payslips']
      let currentTab = this.props.location.pathname.split('/').pop().toLowerCase()
      if (currentTab === 'payroll') {
        this.props.history.push(
          `/os/identity/${this.props.identityId}/payroll/payrollruns`
        )
        this.props.onTabClick('payrollruns')  // initialise default view
      }
      let initialActiveTabIndex = ['payrollruns', 'payslips'].indexOf(currentTab)

      return (
        <ScopedTabbedView
          tabs={subTabs}
          contents={[]}
          onClick={tabIndex => {
            let productTab = subTabs[tabIndex].toLowerCase().replace(' ', '')
            this.props.history.push(
              `/os/identity/${this.props.identityId}/payroll/${productTab}`
            )
            this.props.onTabClick(productTab)
          }}
          initialActiveTabIndex={initialActiveTabIndex > -1
            ? initialActiveTabIndex
            : null
          }
        />
      )
    } else {
      return null
    }
  }

  render() {
    let primaryTabs = this.getPrimaryTabs(this.props.identityTypes)
    let primaryTabNames = Object.values(primaryTabs).map(tab => tab.name)
    let primaryTabLabels = Object.values(primaryTabs).map(tab => tab.label)
    let selectedTab = primaryTabNames.indexOf(this.state.activePrimaryTab)

    return (
      <React.Fragment>
        <div className='u-bg-color-white'>
          <TabbedView
            tabs={primaryTabLabels}
            contents={[]}
            selectedTab={selectedTab}
            onClick={tabIndex => {
              let tab = primaryTabs[tabIndex].name
              this.setState({ activePrimaryTab: tab }, () => (
                this.props.history.push(`/os/identity/${this.props.identityId}/${tab}`)
              ))
              if (hasSecondaryTabs(tab)) {
                this.props.onTabClick(null)
              } else {
                this.props.onTabClick(tab)
              }
            }}
            tabsClassName='slds-align_absolute-center'
            alwaysFireOnClick={true}
          />
        </div>
        {this.getSecondaryTabs(this.state.activePrimaryTab)}
      </React.Fragment>
    )
  }
}

export default withRouter(IdentityPageNavigation)



// WEBPACK FOOTER //
// ./src/components/identity-page-navigation/index.js